import React, { useState } from "react";

import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ListSubheader,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { RushaIcon } from "@shell/components/icons";
import { PLATFORM_PRODUCTS } from "src/platform/constants/routes";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";

/**
 * Known platform products surfaced in the Apps launcher (S15).
 *
 * Mirrors the read-only product catalog rendered on the Platform Products
 * page. Product lifecycle and binding state are intentionally not loaded by
 * the shell launcher.
 */
interface ProductEntry {
  productKey: string;
  name: string;
  category: string;
  available: boolean;
}

const PRODUCTS: ProductEntry[] = [
  {
    productKey: "llm-gateway",
    name: "LLM Gateway",
    category: "AI infrastructure",
    available: true,
  },
  {
    productKey: "customer-identity",
    name: "Customer Identity",
    category: "Application identity",
    available: false,
  },
];

/**
 * Styled product mark (S15).
 *
 * Available products render a filled blue mark; unavailable products render a
 * neutral outlined mark.
 */
const ProductMark: React.FC<{ available: boolean }> = ({ available }) => {
  const theme = useTheme();
  return (
    <Box
      aria-hidden
      sx={{
        width: 22,
        height: 22,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8rem",
        fontWeight: 700,
        lineHeight: 1,
        ...(available
          ? {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
            }
          : {
              bgcolor: "transparent",
              color: "text.secondary",
              border: `1px solid ${theme.palette.divider}`,
            }),
      }}
    >
      ✦
    </Box>
  );
};

const AppsLauncher: React.FC = () => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias } = useActiveOrganization();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProduct = (productKey: string) => {
    if (!orgAlias) return;
    handleClose();
    appNavigation.goToPlatformProductDetails(orgAlias, productKey);
  };

  const handlePlatformProducts = () => {
    if (!orgAlias) return;
    handleClose();
    appNavigation.goToPlatformProducts(orgAlias);
  };

  const handleOrganization = () => {
    handleClose();
    appNavigation.goToOrganizations();
  };

  const handleProjects = () => {
    if (!orgAlias) return;
    handleClose();
    appNavigation.goToProjects(orgAlias);
  };

  return (
    <>
      <Tooltip title="Open apps">
        <IconButton
          onClick={handleOpen}
          aria-label="Open apps"
          aria-controls={open ? "apps-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            color: "text.secondary",
            "&:hover": {
              color: "text.primary",
              bgcolor: "action.hover",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <RushaIcon name="apps" size={20} />
        </IconButton>
      </Tooltip>

      <Menu
        id="apps-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-label": "Apps" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 286,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <ListSubheader component="div" sx={{ bgcolor: "transparent" }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Apps
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {orgAlias
              ? `Organization: ${orgAlias}`
              : "Select an organization to continue"}
          </Typography>
        </ListSubheader>

        {/* Product discovery is deliberately read-only in the shell. */}
        <ListSubheader component="div" sx={{ bgcolor: "transparent", pt: 1 }}>
          Platform products
        </ListSubheader>
        {PRODUCTS.map((product) => {
          return (
            <MenuItem
              key={product.productKey}
              disabled={!orgAlias || !product.available}
              onClick={
                product.available
                  ? () => handleProduct(product.productKey)
                  : undefined
              }
              aria-label={product.name}
            >
              <ListItemIcon>
                <ProductMark available={product.available} />
              </ListItemIcon>
              <ListItemText
                primary={product.name}
                secondary={
                  !orgAlias
                    ? "Organization required"
                    : product.available
                      ? "Read-only details"
                      : "Not available in this release"
                }
              />
            </MenuItem>
          );
        })}

        <Divider />
        <ListSubheader component="div" sx={{ bgcolor: "transparent", pt: 1 }}>
          Browse catalog
        </ListSubheader>
        <MenuItem disabled={!orgAlias} onClick={handlePlatformProducts}>
          <ListItemText primary={PLATFORM_PRODUCTS.CATALOG.title} />
        </MenuItem>

        <ListSubheader component="div" sx={{ bgcolor: "transparent", pt: 1 }}>
          Recently visited
        </ListSubheader>
        <MenuItem onClick={handleOrganization}>
          <ListItemText primary="Organization details" />
        </MenuItem>
        <MenuItem disabled={!orgAlias} onClick={handleProjects}>
          <ListItemText primary="Projects" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default AppsLauncher;
